import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from 'node_modules/@angular/forms';
import { DateRange } from '@synx/date-time';
import { getDateDiff } from 'src/util/date-helpers';
import { PagerOptions } from '../app.types';
import { AuditLog, AuditLogService, IAuditLog } from '../services/audit-log/audit-log.service';
import { UsersService } from '../services/users/users.service';
import { DeviceManagementService } from '../services/device-management/device-management.service';
import { filter, map } from 'rxjs/operators';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'user-audit-log-page',
    templateUrl: './user-audit-log-page.component.html',
    styleUrls: ['./user-audit-log-page.component.scss'],
    standalone: false
})
export class UserAuditLogPageComponent implements OnInit {
  maxDate = new Date();
  form = new FormGroup({
    logDateRange: new FormControl(new DateRange(new Date(Date.now() - 24 * 60 * 60 * 1000), new Date())),
    users: new FormControl(),
    assets: new FormControl(),
    activities: new FormControl()
  });

  // Sentinel user id used by the backend for logs generated by cloud
  // processes (i.e. with no associated user). Always included in the fetch so
  // the audit-logging-table can toggle their visibility.
  private static readonly CLOUD_PROCESS_USER_ID = '00000000-0000-0000-0000-000000000000';

  userOptions: { id: string; value: string }[] = [];
  assetOptions: { id: string; value: string }[] = [];
  activityOptions: { id: string; value: string }[] = [];
  auditLogs: AuditLog[] = [];
  isGrouped = false;

  maxAssetCount = 10;

  getDateDiff = getDateDiff;

  constructor(
    private usersService: UsersService,
    private auditLogService: AuditLogService,
    private deviceManagementService: DeviceManagementService
  ) {}

  ngOnInit(): void {
    this.activityOptions = this.auditLogService.ACTIVITIES.map(a => ({
      id: a.value,
      value: a.name,
      colour: a.color
    }));

    forkJoin(
      [
        this.usersService.getUsers(),
        this.deviceManagementService.getDevices({ usePaging: false }).pipe(
          filter(res => res.body?.length),
          map(res => res.body)
        )
      ]
    ).subscribe(([users, devices]) => {
      this.userOptions = users.map(u => ({
        id: u.celUserId,
        value: `${u.firstName} ${u.lastName}`.trim() || u.email
      }));
      this.assetOptions = devices.map(d => ({
        id: d.id,
        value: d.fleetId || d.name || d.serialNumber
      }));

      this.form.patchValue({
        users: this.userOptions.map(u => u.id),
        activities: this.activityOptions.map(a => a.id)
      });

      this.search();
    })
  }

  search(): void {
    const userIds: string[] = [...(this.form.value.users || [])];
    const assetIds: string[] = this.form.value.assets || [];
    const activityIds: string[] = this.form.value.activities || [];
    const startDate = this.form.value.logDateRange?.start || new Date(Date.now() - 24 * 60 * 60 * 1000);
    const endDate = this.form.value.logDateRange?.end || new Date();

    if(assetIds.length > this.maxAssetCount) {
      alert(`Please select at most ${this.maxAssetCount} assets to filter by. You have selected ${assetIds.length}.`);
      return;
    }

    userIds.push(UserAuditLogPageComponent.CLOUD_PROCESS_USER_ID);

    // The audit-logging-table paginates client-side, so fetch the full result
    // set. With usePaging=false the endpoint returns a bare array rather than a
    // PagedResults envelope.
    const opts: PagerOptions = {
      usePaging: false,
      search: [
        ...assetIds.map(id => ({ type: 'itemId', searchString: id })),
        ...activityIds.map(id => ({ type: 'activity', searchString: id }))
      ]
    };

    this.auditLogService
      .searchUserAuditLogs(opts, userIds, startDate, endDate)
      .subscribe((response: any) => {
        const items: IAuditLog[] = Array.isArray(response)
          ? response
          : response?.results ?? [];
        this.auditLogs = items.map(r => this.toAuditLog(r));
        this.auditLogs.sort((a, b) => b.date.getTime() - a.date.getTime());
      });
  }

  private toAuditLog(r: IAuditLog): AuditLog {
    return {
      id: r.itemId,
      date: new Date(r.dateTime),
      userId: r.userId,
      user: this.userOptions.find(u => u.id.toUpperCase() === r.userId.toUpperCase())?.value || "Unknown",
      activity: {
        id: r.activity,
        color: this.auditLogService.getActivityColor(r.activity),
        activity: this.auditLogService.ACTIVITIES.find(a => a.value === r.activity)?.name ?? "Unknown",
        caption: this.auditLogService.captionGenerator(r),
        redirection: this.auditLogService.getLogRedirectionUrl(r),
      }
    };
  }

  openUrl(log: AuditLog): void {
    this.auditLogService.openUrl(log);
  }

formatDuration(milliseconds: number): string {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = totalMinutes / 60;
    const totalDays = totalHours / 24;

    if (totalMinutes < 1) {
        return `${totalSeconds}s`;
    } else if (totalHours < 1) {
        const mins = Math.round(totalMinutes * 100) / 100;
        return `${mins} mins`;
    } else if (totalDays < 1) {
        const hours = Math.floor(totalHours);
        const mins = totalMinutes % 60;
        const parts = [];
        if (hours > 0) parts.push(`${hours}h`);
        if (mins > 0) parts.push(`${mins}m`);
        return parts.length > 0 ? parts.join(" ") : "0m";
    } else {
        const days = Math.floor(totalDays);
        const hours = Math.floor(totalHours % 24);
        const mins = totalMinutes % 60;
        const parts = [];
        if (days > 0) parts.push(`${days}d`);
        if (hours > 0) parts.push(`${hours}h`);
        if (mins > 0) parts.push(`${mins}m`);
        return parts.length > 0 ? parts.join(" ") : "0m";
    }
  }

  resetFilters() {
    this.form.patchValue({
      logDateRange: new DateRange(new Date(Date.now() - 24 * 60 * 60 * 1000), new Date()),
      users: this.userOptions.map(u => u.id),
      activities: this.activityOptions.map(a => a.id),
      assets: []
    });
    this.form.markAsPristine();
    this.search();
  }

  toggleIsGrouped() {
    this.isGrouped = !this.isGrouped;
  }
}
